import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';

import {
  addResidenceHistory,
  resetResidenceHistory,
  persistResidenceHistory,
  addPageData,
  navigateWizard,
} from '../../actions';
import Button from '../../components/Button';
import PageLayout from '../../components/PageLayout';
import YearMonthPicker from '../../components/YearMonthPicker';
import analytics from '../../lib/analytics';
import { fullDateFormat } from '../../lib/dateFormatter';
import { isFutureMonth } from '../../lib/dateOrderCheck';
import useCountries from '../../lib/hooks/useCountries';
import useStates from '../../lib/hooks/useStates';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';

const CurrentResidenceDatesPage = ({
  initialCurrentResidenceMonth,
  initialCurrentResidenceYear,
  dispatchAddResidenceHistory,
  dispatchResetResidenceHistory,
  dispatchPersistResidenceHistory,
  dispatchAddPageData,
  dispatchWizardNav,
}) => {
  const { selectedResidence: selectedCountry } = useCountries();
  const { selected: selectedState } = useStates();
  const [year, setYear] = useState(initialCurrentResidenceYear);
  const [month, setMonth] = useState(initialCurrentResidenceMonth);

  const currentYear = new Date(Date.now()).getFullYear();

  const changeMonth = event => {
    setMonth(event.target.value);
  };

  const changeYear = event => {
    setYear(event.target.value);
  };

  const handleSubmit = event => {
    event.preventDefault();

    if (isInvalid()) {
      return;
    }

    analytics.sendEvent('Click - Next', 'current-residence-dates');

    const currentResidenceStartDate = new Date(year, month, 1);

    const payload = payloadFactory.createAddPageDataPayload(
      'currentResidenceStartDate',
      { month, year },
      'currentResidenceStartDate',
      fullDateFormat(currentResidenceStartDate),
    );
    dispatchAddPageData(payload);
    const residence = {
      countryCode: selectedCountry.value,
      stateCode: selectedState.value,
      startMonth: parseInt(month, 10),
      startYear: parseInt(year, 10),
    };
    dispatchResetResidenceHistory();
    dispatchAddResidenceHistory(residence);
    dispatchPersistResidenceHistory();
    dispatchWizardNav({ dir: 'fwd' });
  };

  const isInvalid = () => {
    const nowDate = new Date(Date.now());
    const paramMonth = parseInt(month, 10);
    const paramYear = parseInt(year, 10);
    return (
      isNaN(paramMonth) ||
      isNaN(paramYear) ||
      isFutureMonth(nowDate.getMonth(), nowDate.getFullYear(), paramMonth, paramYear)
    );
  };

  const stateText = selectedState.label ? `in ${selectedState.label}` : 'there';

  return (
    <PageLayout title={`When did you start living ${stateText}?`}>
      <form className="row-spacer" onSubmit={handleSubmit}>
        <div className="row">
          <div className="col col--8/16-small">
            <YearMonthPicker
              month={month}
              year={year}
              minYear={1990}
              maxYear={currentYear}
              onMonthChange={changeMonth}
              onYearChange={changeYear}
              yearId="year-from-select"
              monthId="month-from-select"
              allowFutureDates={false}
            />
          </div>

          <div className="col col--auto">
            <Button type="submit" isDisabled={isInvalid()}>
              Next
            </Button>
          </div>
        </div>
      </form>
    </PageLayout>
  );
};

CurrentResidenceDatesPage.propTypes = {
  initialCurrentResidenceMonth: PropTypes.string.isRequired,
  initialCurrentResidenceYear: PropTypes.string.isRequired,
  dispatchAddResidenceHistory: PropTypes.func.isRequired,
  dispatchResetResidenceHistory: PropTypes.func.isRequired,
  dispatchPersistResidenceHistory: PropTypes.func.isRequired,
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
};

export const mapStateToProps = reduxStore => ({
  initialCurrentResidenceMonth: reduxStore.builder.ui.currentResidenceStartDate.month,
  initialCurrentResidenceYear: reduxStore.builder.ui.currentResidenceStartDate.year,
});

export const mapDispatchToProps = {
  dispatchAddResidenceHistory: addResidenceHistory,
  dispatchResetResidenceHistory: resetResidenceHistory,
  dispatchPersistResidenceHistory: persistResidenceHistory,
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export { CurrentResidenceDatesPage };

export default connect(mapStateToProps, mapDispatchToProps)(CurrentResidenceDatesPage);
